import { Box, SxProps, Theme } from "@mui/material";
import { keyframes } from "@mui/system";
import { ReactNode } from "react";

interface MarqueeProps {
  items: ReactNode[];
  gap?: number | { xs?: number; md?: number };
  paddingX?: number | { xs?: number; md?: number };
  paddingY?: number | { xs?: number; md?: number };
  animationDuration?: string;
  width?: string | number;
}

const Marquee = ({
  items,
  gap = { xs: 3, md: 5 },
  paddingX = { xs: 2, md: 4 },
  paddingY = { xs: 3, md: 4 },
  animationDuration = "28s",
  width = "800px",
}: MarqueeProps) => {
  // Duplicate items for seamless infinite scroll
  const marqueeItems = [...items, ...items];

  return (
    <Box sx={styles.container(width)}>
      {/* Fade overlay */}
      <Box sx={styles.fadeOverlay} />
      {/* Marquee content */}
      <Box sx={styles.content(gap, paddingX, paddingY, animationDuration)}>
        {marqueeItems.map((item, index) => (
          <Box key={`marquee-item-${index}`} sx={styles.itemWrapper}>
            {item}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Marquee;

const marqueeAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

const styles = {
  container: (width: string | number): SxProps<Theme> => ({
    mx: "auto",
    width,
    overflow: "hidden",
    position: "relative",
  }),
  fadeOverlay: {
    position: "absolute" as const,
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: 10,
    backgroundImage: "linear-gradient(90deg, #000, #2b2b2b00 15%, #34343400 85%, #000)",
    pointerEvents: "none" as const,
  },
  content: (
    gap: number | { xs?: number; md?: number },
    paddingX: number | { xs?: number; md?: number },
    paddingY: number | { xs?: number; md?: number },
    animationDuration: string
  ): SxProps<Theme> => ({
    display: "flex",
    alignItems: "center",
    gap,
    px: paddingX,
    py: paddingY,
    minWidth: "max-content",
    animation: `${marqueeAnimation} ${animationDuration} linear infinite`,
  }),
  itemWrapper: {
    flexShrink: 0,
  },
};
